import { cn } from '@/common-utils/libraries/tailwind/utils';
import PropTypes from 'prop-types';
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' };

const ChartContext = React.createContext(null);

// Helper to extract item config from a payload.
/**
 * @param config
 * @param payload
 * @param key
 * @returns {JSX}
 */
function getPayloadConfigFromPayload(config, payload, key) {
    if (typeof payload !== 'object' || payload === null) {
        return undefined;
    }

    const payloadPayload =
        'payload' in payload &&
            typeof payload.payload === 'object' &&
            payload.payload !== null
            ? payload.payload
            : undefined;

    let configLabelKey = key;

    if (
        key in payload &&
        typeof payload[key] === 'string'
    ) {
        configLabelKey = payload[key];
    } else if (
        payloadPayload &&
        key in payloadPayload &&
        typeof payloadPayload[key] === 'string'
    ) {
        configLabelKey = payloadPayload[key];
    }

    return configLabelKey in config
        ? config[configLabelKey]
        : config[key];
}

/**
 * @returns {JSX}
 */
function useChart() {
    const context = React.useContext(ChartContext);

    if (!context) {
        throw new Error('useChart must be used within a <ChartContainer />');
    }

    return context;
}

/**
 * @param root0
 * @param root0.id
 * @param root0.className
 * @param root0.children
 * @param root0.config
 * @returns {JSX}
 */
function ChartContainer({
    id,
    className,
    children,
    config,
    ...props
}) {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;
    const chartConfig = React.useMemo(() => config, [config]);

    const contextValue = React.useMemo(() => ({ chartConfig }), [chartConfig]);

    return (
        <ChartContext.Provider value={contextValue}>
            <div
                data-slot='chart'
                data-chart={chartId}
                className={cn(
                    'outline-none focus:outline-none focus:ring-0',
                    // disable focus on all internal elements (SVG included)
                    '[&_*:focus]:outline-none',
                    '[&_*:focus]:ring-0',
                    // recharts fixes to remove focus
                    '[&_.recharts-layer]:outline-none',
                    '[&_.recharts-surface]:outline-none',
                    '[&_.recharts-sector]:outline-none',
                    // optional (avoid text selection on double click)
                    '[&_*]:select-none',
                    'pointer-events-auto',
                    '[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke-border/50]]:stroke-border/50',
                    ' [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke-border/50]]:stroke-border',
                    '[&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted',
                    '[&_.recharts-reference-line_[stroke-border/50]]:stroke-border flex aspect-video justify-center text-xs',
                    '[&_.recharts-dot[stroke=white]]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden',
                    '[&_.recharts-sector[stroke=white]]:stroke-transparent [&_.recharts-surface]:outline-hidden',
                    className
                )}
                tabIndex={-1}
                {...props}
            >
                <ChartStyle id={chartId} config={config} />
                <RechartsPrimitive.ResponsiveContainer>
                    {children}
                </RechartsPrimitive.ResponsiveContainer>
            </div>
        </ChartContext.Provider>
    );
}

const ChartStyle = ({ id, config }) => {
    const colorConfig = Object.entries(config).filter(
        ([, conf]) => conf.theme || conf.color
    );

    if (!colorConfig.length) {
        return null;
    }

    return (
        <style
            dangerouslySetInnerHTML={{
                __html: Object.entries(THEMES)
                    .map(
                        ([theme, prefix]) => `${prefix} [data-chart=${id}] {${colorConfig
                            .map(([key, itemConfig]) => {
                                const color =
                                    itemConfig.theme?.[theme] || itemConfig.color;
                                return color ? `  --color-${key}: ${color};` : null;
                            })
                            .join('\n')}}`
                    )
                    .join('\n')
            }}
        />
    );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

/**
 * @param root0
 * @param root0.active
 * @param root0.payload
 * @param root0.className
 * @param root0.indicator
 * @param root0.hideLabel
 * @param root0.hideIndicator
 * @param root0.label
 * @param root0.labelFormatter
 * @param root0.labelClassName
 * @param root0.formatter
 * @param root0.color
 * @param root0.nameKey
 * @param root0.labelKey
 * @returns {JSX}
 */
function ChartTooltipContent({
    active,
    payload,
    className,
    indicator = 'dot',
    hideLabel = false,
    hideIndicator = false,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey
}) {
    const { chartConfig: config } = useChart();

    const tooltipLabel = React.useMemo(() => {
        if (hideLabel || !payload?.length) {
            return null;
        }

        const [item] = payload;
        const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        const value =
            !labelKey && typeof label === 'string'
                ? config[label]?.label || label
                : itemConfig?.label;

        if (labelFormatter) {
            return (
                <div className={cn('font-medium', labelClassName)}>
                    {labelFormatter(value, payload)}
                </div>
            );
        }

        if (!value) {
            return null;
        }

        return <div className={cn('font-medium', labelClassName)}>{value}</div>;
    }, [
        label,
        labelFormatter,
        payload,
        hideLabel,
        labelClassName,
        config,
        labelKey
    ]);

    if (!active || !payload?.length) {
        return null;
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
        <div
            className={cn(
                'border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl',
                className
            )}
        >
            {!nestLabel ? tooltipLabel : null}
            <div className='grid gap-1.5'>
                {payload
                    .filter((item) => item.type !== 'none')
                    .map((item, index) => {
                        const key = `${nameKey || item.name || item.dataKey || 'value'}`;
                        const itemConfig = getPayloadConfigFromPayload(config, item, key);
                        const indicatorColor = color || item.payload.fill || item.color;

                        return (
                            <div
                                key={item.dataKey}
                                className={cn(
                                    '[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5',
                                    indicator === 'dot' && 'items-center'
                                )}
                            >
                                {formatter && item?.value !== undefined && item.name ? (
                                    formatter(item.value, item.name, item, index, item.payload)
                                ) : (
                                    <>
                                        {itemConfig?.icon ? (
                                            <itemConfig.icon />
                                        ) : (
                                            !hideIndicator && (
                                                <div
                                                    className={cn(
                                                        'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
                                                        {
                                                            'h-2.5 w-2.5': indicator === 'dot',
                                                            'w-1': indicator === 'line',
                                                            'w-0 border-[1.5px] border-dashed bg-transparent':
                                                                indicator === 'dashed',
                                                            'my-0.5': nestLabel && indicator === 'dashed'
                                                        }
                                                    )}
                                                    style={{
                                                        '--color-bg': indicatorColor,
                                                        '--color-border': indicatorColor
                                                    }}
                                                />
                                            )
                                        )}
                                        <div
                                            className={cn(
                                                'flex flex-1 justify-between leading-none',
                                                nestLabel ? 'items-end' : 'items-center'
                                            )}
                                        >
                                            <div className='grid gap-1.5'>
                                                {nestLabel ? tooltipLabel : null}
                                                <span className='text-muted-foreground'>
                                                    {itemConfig?.label || item.name}
                                                </span>
                                            </div>
                                            {item.value && (
                                                <span className='text-foreground font-mono font-medium tabular-nums'>
                                                    {item.value.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}

const ChartLegend = RechartsPrimitive.Legend;

/**
 * @param root0
 * @param root0.className
 * @param root0.hideIcon
 * @param root0.payload
 * @param root0.verticalAlign
 * @param root0.nameKey
 * @returns {JSX}
 */
function ChartLegendContent({
    className,
    hideIcon = false,
    payload,
    verticalAlign = 'bottom',
    nameKey
}) {
    const { chartConfig: config } = useChart();

    if (!payload?.length) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex items-center justify-center gap-4',
                verticalAlign === 'top' ? 'pb-3' : 'pt-3',
                className
            )}
        >
            {payload
                .filter((item) => item.type !== 'none')
                .map((item) => {
                    const key = `${nameKey || item.dataKey || 'value'}`;
                    const itemConfig = getPayloadConfigFromPayload(config, item, key);

                    return (
                        <div
                            key={item.value}
                            className={cn(
                                '[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3'
                            )}
                        >
                            {itemConfig?.icon && !hideIcon ? (
                                <itemConfig.icon />
                            ) : (
                                <div
                                    className='h-2 w-2 shrink-0 rounded-[2px]'
                                    style={{
                                        backgroundColor: item.color
                                    }}
                                />
                            )}
                            {itemConfig?.label}
                        </div>
                    );
                })}
        </div>
    );
}

ChartContainer.propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    config: PropTypes.object
};

ChartStyle.propTypes = {
    id: PropTypes.string,
    config: PropTypes.object
};

ChartTooltipContent.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    className: PropTypes.string,
    indicator: PropTypes.string,
    hideLabel: PropTypes.bool,
    hideIndicator: PropTypes.bool,
    label: PropTypes.string,
    labelFormatter: PropTypes.func,
    labelClassName: PropTypes.string,
    formatter: PropTypes.func,
    color: PropTypes.string,
    nameKey: PropTypes.string,
    labelKey: PropTypes.string
};

ChartLegendContent.propTypes = {
    className: PropTypes.string,
    hideIcon: PropTypes.bool,
    payload: PropTypes.array,
    verticalAlign: PropTypes.string,
    nameKey: PropTypes.string
};

export {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
    ChartStyle
};